/* eslint-disable spire/fenced-imports */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OffsetOptions } from "@floating-ui/dom";
import { useId } from "@floating-ui/react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import FancyScroll from "@keller/App/Components/Common/FancyScroll";
import ClientAuthoredContent, { ClientAuthoredContentProps } from "@keller/App/Components/Header/ClientAuthoredContent";
import FloutingUiMenu, { MenuItem, PANEL_MAX_WIDTH_VAR } from "@keller/App/Components/Menu/DropdownMenu";
import { color, ellipsis, styledScrollbar, xClassNames } from "@keller/XDL/Utilities";
import React, { FC, memo, useCallback } from "react";
import styled, { ThemeProps, withTheme } from "styled-components";

const DEFAULT_BOUNDARY_SELECTOR = "#header-container";

export type MenuVariants = "primary" | "secondary";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    /**
     * XNG-Changes:
     * onClick callback method
     */
    onClick?: () => void;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /**
         * XNG-Changes
         * For Client-Authored Content component. it appears when level 3 appear.
         * it's authoring component. we defended it inside MainNavigation widget
         */
        clientAuthoredContent?: ClientAuthoredContentProps;
        /**
         * XNG-Changes
         */
        referenceIndex?: number;
        menuVariant?: MenuVariants;
        offset?: OffsetOptions;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        // XNG-Changes
        referenceIndex?: number;
    };

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "common.ui6",
    },
    active: {
        background: "common.ui1",
        text: "common.ui1",
        icon: "common.ui1",
    },
};

// xng-changes
const MenuStyle = styled.div<MenuStyleProps>`
    background: ${color("common.white")};
    width: ${getProp("width", 175)}px;
    width: 325px;
    max-width: min(25vw, var(${PANEL_MAX_WIDTH_VAR}, 25vw));
    min-height: 61px;
    height: 450px;
    max-height: calc(100vh - 280px);
    padding: 16px 0;
    list-style: none;
    margin: 0;
    overflow: auto;
    border: 1px solid ${color("common.border")};

    &[data-max-depth="1"] {
        height: auto;
    }

    & > .MenuItem[data-open] {
        > [data-test-selector="menuItem"]:not(hover):not([data-depth="3"]),
        > [data-test-selector="menuItem"]:not(.has-children):not([data-depth="3"]) {
            background: ${color(menuItemColorStyle.active.background, 0.15)};
            > ${StyledButton} > ${TypographyStyle} {
                color: ${color("common.ui1")};
            }
            ${IconWrapper} {
                color: ${color(menuItemColorStyle.active.icon)};
            }
        }
    }

    ${styledScrollbar(5)}
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;

export const MenuWrapper = styled.nav<MenuWrapperProps>`
    .trigger-item,
    .arrow {
        transition: transform ease-in-out 250ms;
        color: ${color("common.white")};
        background-color: transparent;
    }

    &:hover {
        .trigger-item {
            background: ${color("common.ui1")};
        }
        .arrow {
            color: ${color("common.white")};
        }
    }

    [data-state="open"] &,
    &[data-open] {
        .arrow {
            transform: rotate(180deg);
        }
        .trigger-item {
            background: ${color("common.ui1")};
        }
    }

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${color("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 20px;
    ${ellipsis(2)}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick?: () => void;
        hasChildren?: boolean;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > a {
        padding: 8px 16px;
    }

    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: inline-block;
        width: calc(100% - 15px);
    }

    ${IconWrapper} {
        color: ${color(menuItemColorStyle.default.icon)};
    }

    &:hover {
        background: ${(props: any) =>
            props.hasChildren
                ? color(menuItemColorStyle.active.background, 0.33)(props)
                : color(menuItemColorStyle.active.background, 0.15)(props)};
        &[data-depth="3"] {
            background: ${color(menuItemColorStyle.active.background, 0.15)};
        }

        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }
    ${injectCss}
`;

const RenderMenuItems: FC<{
    childMenuItems: MappedLink[];
    currentDepth: number;
    maxDepth: number;
    width?: number;
    applyProp: any;
    applyStyledProp: any;
    resolvedMergeCss: boolean | undefined;
    displayChangeCustomerLink?: boolean;
    clientAuthoredContent?: ClientAuthoredContentProps;
    renderMenuItems: (items: MappedLink[], depth: number) => React.ReactNode;
    menuVariant?: MenuVariants;
}> = memo(
    ({
        childMenuItems,
        currentDepth,
        maxDepth,
        width,
        applyProp,
        applyStyledProp,
        resolvedMergeCss,
        displayChangeCustomerLink,
        clientAuthoredContent,
        renderMenuItems,
        menuVariant,
    }) => {
        const id = useId();
        return (
            <React.Fragment key={id}>
                {childMenuItems
                    ?.filter(item => !item.excludeFromNavigation)
                    .map((item, index) => {
                        /**
                         * XNG-Changes
                         */
                        const itemMaxDepth = item.maxDepth || maxDepth;
                        const hasChildren =
                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < itemMaxDepth;

                        if (item.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
                            return null;
                        }

                        const itemElement = (
                            <MenuItem
                                targetComponent={
                                    <MenuItemStyle
                                        width={width}
                                        hasChildren={hasChildren}
                                        css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                        key={`${item.title}_${index}`}
                                        className={xClassNames.append(["MenuItem", hasChildren && "has-children"])}
                                        data-test-selector="menuItem"
                                        data-depth={currentDepth}
                                    >
                                        <Clickable
                                            href={item.url}
                                            onClick={() => item.onClick?.()}
                                            target={item.openInNewWindow ? "_blank" : ""}
                                            {...applyProp("menuItemClickableProps")}
                                        >
                                            <MenuItemText
                                                hasChildren={hasChildren}
                                                {...applyProp("menuItemTypographyProps")}
                                            >
                                                {item.title}
                                            </MenuItemText>
                                            {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                        </Clickable>
                                    </MenuItemStyle>
                                }
                            />
                        );

                        if (hasChildren) {
                            return (
                                <FloutingUiMenu
                                    key={`FloutingUiMenu_${item.title}_${index}`}
                                    menuTrigger={itemElement}
                                    placement="right-start"
                                    offset={{ mainAxis: 0, crossAxis: -1 }}
                                    shift={{
                                        boundary:
                                            typeof document !== "undefined"
                                                ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                                : "clippingAncestors",
                                    }}
                                    variant={menuVariant}
                                >
                                    {renderMenuItems(item!.children!, currentDepth + 1)}
                                </FloutingUiMenu>
                            );
                        }

                        return <StyledWrapper key={`MenuItem_${item.title}_${id}`}>{itemElement}</StyledWrapper>;
                    })}
                {currentDepth === 3 && <ClientAuthoredContent {...clientAuthoredContent} width={width} />}
            </React.Fragment>
        );
    },
);

// Mirrors RenderMenuItems' hasChildren rule so the count matches the panels actually rendered.
const getCascadeDepth = (items: MappedLink[] | undefined, depth: number, maxDepth: number): number => {
    const visibleItems = items?.filter(item => !item.excludeFromNavigation) ?? [];

    return visibleItems.reduce((deepest, item) => {
        const itemMaxDepth = item.maxDepth || maxDepth;
        const hasChildren = item.children?.some(o => !o.excludeFromNavigation) && depth < itemMaxDepth;

        return hasChildren ? Math.max(deepest, getCascadeDepth(item.children, depth + 1, maxDepth)) : deepest;
    }, depth);
};

const Menu: FC<MenuProps> = ({
    menuItems,
    menuTrigger,
    isOpen,
    mergeCss,
    menuVariant,
    referenceIndex,
    offset,
    clientAuthoredContent,
    theme,
    ...otherProps
}) => {
    const { applyProp, applyStyledProp } = applyPropBuilder({ ...otherProps, theme }, { component: "menu" });
    const resolvedMergeCss = mergeCss ?? theme?.menu?.defaultProps?.mergeCss;
    const { maxDepth = 3, width = 325, displayChangeCustomerLink } = otherProps;
    const descriptionId = otherProps.descriptionId || uniqueId();

    const cascadeDepth = getCascadeDepth(menuItems, 1, maxDepth);
    // Client-authored content renders as an extra panel to the right of level 3.
    const hasAuthoredPanel = cascadeDepth >= 3 && !!clientAuthoredContent?.title && !!clientAuthoredContent?.imageUrl;
    const panelCount = cascadeDepth + (hasAuthoredPanel ? 1 : 0);

    const renderMenuItems = useCallback(
        (childMenuItems: MappedLink[], currentDepth: number) => {
            const menuStyleRef = React.createRef<HTMLDivElement>();

            return (
                <FancyScroll refElement={menuStyleRef}>
                    <MenuStyle
                        ref={menuStyleRef}
                        width={width}
                        referenceIndex={referenceIndex}
                        // XNG-Changes
                        data-max-depth={currentDepth}
                        css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                    >
                        <RenderMenuItems
                            childMenuItems={childMenuItems}
                            currentDepth={currentDepth}
                            maxDepth={maxDepth}
                            width={width}
                            applyProp={applyProp}
                            applyStyledProp={applyStyledProp}
                            resolvedMergeCss={resolvedMergeCss}
                            displayChangeCustomerLink={displayChangeCustomerLink}
                            clientAuthoredContent={clientAuthoredContent}
                            renderMenuItems={renderMenuItems}
                            menuVariant={menuVariant}
                        />
                    </MenuStyle>
                </FancyScroll>
            );
        },
        [applyStyledProp, resolvedMergeCss, width, displayChangeCustomerLink, maxDepth, clientAuthoredContent],
    );

    return (
        <FloutingUiMenu
            trigger="hover"
            overlay={true}
            offset={offset || { mainAxis: 0 }}
            placement="bottom-start"
            cascadeDepth={panelCount}
            menuTrigger={
                <MenuWrapper
                    css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                    {...{ "aria-labelledby": descriptionId }}
                >
                    {React.cloneElement(menuTrigger, {
                        id: descriptionId,
                        className: "trigger trigger-item",
                    })}
                </MenuWrapper>
            }
            variant={menuVariant}
        >
            {renderMenuItems(menuItems, 1)}
        </FloutingUiMenu>
    );
};

/** @component */
// @ts-ignore: Type instantiation is excessively deep and possibly infinite.
export default withTheme(Menu);

export { MenuItemText };
